import { useEffect, useMemo, useState } from "react";
import { Row, Card, CardBody } from "reactstrap";
import { motion } from "framer-motion";
import ReactApexChart from "react-apexcharts";

import {
  getMcapOrgTotals,
  getMcapAdoptionMetrics,
  getMcapContentSummary,
  getMcapTeacherEngagement,
  getMcapUsageTrends,
} from "api/api.service";
import { getCached, setCached, mcapCacheKey } from "utils/mcapCache";
import EmptyState from "../components/EmptyState";
import DonutChart from "../components/DonutChart";
import { CONTEXT_LABELS } from "../labels";
import typography from "../typography";

const panelVariants = { hidden: { opacity: 0, scale: 0.98 }, visible: { opacity: 1, scale: 1, transition: { duration: 0.3, ease: [0.23, 1, 0.32, 1] } } };
const GRAPH_COLORS = { teal: "#06402b", orange: "#e0b463", gridBorder: "#f6f6f6" };

// Fixed hue per moment rather than a generated palette — an admin who looks
// at this donut every week should see "Transition" as the same colour every
// time, not whatever slot it happened to sort into. The five real
// ResetContext values plus a neutral for "no moment recorded" — activities
// launched outside a teacher-picked context (most built-in launches).
const MOMENT_COLORS = {
  MORNING_MEETING: "#227f5d",
  TRANSITION: "#1f6f8b",
  AFTER_LUNCH: "#6f42c1",
  RETURN_FROM_RECESS: "#b5563b",
  BEFORE_TEST: "#c9972b",
  UNSPECIFIED: "#8a9992",
};

const AUTH_METHOD_LABELS = {
  PASSWORD: "Email & password",
  GOOGLE: "Google sign-in",
  CLEVER: "Clever sign-in",
  MULTIPLE: "More than one method",
  OTHER: "Other",
};
const AUTH_METHOD_COLORS = {
  PASSWORD: "#227f5d",
  GOOGLE: "#c9972b",
  CLEVER: "#1f6f8b",
  MULTIPLE: "#6f42c1",
  OTHER: "#8a9992",
};

// A generic fetch-into-state effect — org-totals/adoption already repeat
// this shape inline above (kept as-is rather than retrofitted, to avoid
// touching working code for its own sake); the three new widgets below share
// it instead of copy-pasting a fourth/fifth/sixth near-identical effect.
function useMcapQuery(fetchFn, cacheNamespace, filters) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const cacheKey = mcapCacheKey(cacheNamespace, filters);
    const cached = getCached(cacheKey);
    if (cached) {
      setData(cached);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    fetchFn(filters)
      .then((res) => {
        if (cancelled) return;
        setData(res || null);
        setCached(cacheKey, res);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters)]);

  return { data, loading };
}

// Big-number hero card: a title bar, one headline stat, an optional plain
// context line (never a fabricated up/down trend — none of this data has a
// prior-period baseline computed server-side, so claiming a direction we
// can't back up would be exactly the kind of invented number the rest of
// this dashboard has been careful to avoid), then a divided breakdown list.
// Every value here already exists in org-totals/adoption-metrics or is a
// client-side aggregation of that same response (e.g. distinct district
// count) — no new backend call, no new figure.
function HeroStatCard({ title, infoText, value, loading, contextText, rows }) {
  return (
    <div className="col-xl-4 col-md-6 col-12 mb-3">
      <Card className="mcap-hero-card h-100 mb-0">
        <div className="mcap-hero-card-header">
          <span className="standard-typo-14-500-ld mcap-hero-card-title">{title}</span>
          {infoText && (
            <span className="mcap-hero-card-info" title={infoText}>
              i
            </span>
          )}
        </div>
        <CardBody>
          <p className="mcap-hero-value mb-1">{loading ? "—" : value}</p>
          {!loading && contextText && <p className="mcap-hero-context mb-0">{contextText}</p>}
          {!loading && rows && rows.length > 0 && (
            <div className="mcap-hero-rows">
              {rows.map((r, i) => (
                <div className="mcap-hero-row" key={i}>
                  <span className="mcap-hero-row-label">{r.label}</span>
                  <span className="mcap-hero-row-value">{r.value}</span>
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

// Weekly series come back sparse-and-suppressed (see WeeklyCountItem) — every
// week in [minWeek, maxWeek] is rendered, suppressed ones as null so Apex
// draws a gap rather than a misleading zero.
function alignWeeklySeries(a, b) {
  const weeks = [...new Set([...a, ...b].map((item) => item.weekStart))].sort();
  const byWeek = (arr) => new Map(arr.map((item) => [item.weekStart, item]));
  const aMap = byWeek(a);
  const bMap = byWeek(b);
  return {
    weeks,
    aValues: weeks.map((w) => aMap.get(w)?.count ?? null),
    bValues: weeks.map((w) => bMap.get(w)?.count ?? null),
  };
}

export default function Overview({ filters }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adoption, setAdoption] = useState(null);
  const [adoptionLoading, setAdoptionLoading] = useState(true);

  // Three widgets an administrator asked for on this screen specifically —
  // each already had a live backend endpoint before today, just no consumer.
  // See ResetsAndContent for the detailed per-day/hour/moment breakdown this
  // donut echoes at a glance, and Implementation for the school-level cut of
  // the same reset data — this tab stays platform-wide, no school/teacher.
  const { data: contentSummary, loading: contentLoading } = useMcapQuery(getMcapContentSummary, "content-summary", filters);
  // role:Educator-scoped teacher web-portal engagement — not the generic
  // engagement/summary endpoint, which at platform-wide scope also counts
  // the separate mobile companion app's own users (see the DTO's own
  // comment). This is the same population org-totals/adoption-metrics use.
  const { data: engagement, loading: engagementLoading } = useMcapQuery(getMcapTeacherEngagement, "teacher-engagement", filters);
  const { data: usageTrends, loading: usageLoading } = useMcapQuery(getMcapUsageTrends, "usage-trends", filters);

  const [completionView, setCompletionView] = useState("rate");
  const [engagementView, setEngagementView] = useState("trend");

  useEffect(() => {
    let cancelled = false;
    const cacheKey = mcapCacheKey("org-totals", filters);
    const cached = getCached(cacheKey);
    if (cached) {
      setData(cached);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    getMcapOrgTotals(filters)
      .then((res) => {
        if (cancelled) return;
        setData(res || null);
        setCached(cacheKey, res);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters)]);

  useEffect(() => {
    let cancelled = false;
    const cacheKey = mcapCacheKey("adoption", filters);
    const cached = getCached(cacheKey);
    if (cached) {
      setAdoption(cached);
      setAdoptionLoading(false);
      return undefined;
    }
    setAdoptionLoading(true);
    getMcapAdoptionMetrics(filters)
      .then((res) => {
        if (cancelled) return;
        setAdoption(res || null);
        setCached(cacheKey, res);
      })
      .catch(() => {
        if (!cancelled) setAdoption(null);
      })
      .finally(() => {
        if (!cancelled) setAdoptionLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters)]);

  const hasData = !!data && data.totalResets > 0;
  // The full school-by-school comparison table this used to feed moved to
  // Implementation — that tab is the school drill-down, and repeating it
  // here duplicated it one screen over. bySchool itself stays: the
  // Classrooms hero card below still reads district count and the top
  // school from it, which is a platform-wide "outer look" figure, not a
  // per-school breakdown.
  const bySchool = data?.bySchool || [];

  const hasAdoption = !!adoption && adoption.repeatUse.activatedTeachers > 0;
  const weekly = hasAdoption ? alignWeeklySeries(adoption.weeklyActiveTeachers, adoption.weeklyActivations) : { weeks: [], aValues: [], bValues: [] };

  // Client-side aggregation of the same bySchool rows the table below
  // already renders — not a new figure, just a second cut of it for the
  // "Classrooms" card. Suppressed rows are excluded, same as the table.
  const visibleSchools = bySchool.filter((r) => !r.suppressed);
  const districtCount = new Set(visibleSchools.map((r) => r.districtName).filter(Boolean)).size;
  const topSchool = visibleSchools.length
    ? [...visibleSchools].sort((a, b) => (b.completedResets ?? 0) - (a.completedResets ?? 0))[0]
    : null;

  // Reset-moment composition — the platform-wide "what are resets for"
  // question, one donut. Suppressed cells are excluded rather than shown as
  // 0: a moment with genuinely zero resets and a moment too thin to report
  // are different claims, and this chart has no way to distinguish them
  // visually if both draw as an empty wedge.
  const momentSegments = (usageTrends?.byContext ?? [])
    .filter((c) => !c.suppressed && c.count)
    .map((c) => ({
      key: c.context,
      label: c.context === "UNSPECIFIED" ? "No moment recorded" : CONTEXT_LABELS[c.context] || c.context,
      value: c.count,
      color: MOMENT_COLORS[c.context] || MOMENT_COLORS.UNSPECIFIED,
    }));
  const totalMomentResets = momentSegments.reduce((sum, s) => sum + s.value, 0);

  // Video/audio completion — every activity that has ever been started at
  // least once (completionRate null means never started, not 0% complete;
  // excluding those keeps the chart from being mostly empty bars), top 8 by
  // whichever view is active so the chart stays readable regardless of
  // library size.
  const ranked = useMemo(() => {
    const started = (contentSummary?.perActivityBreakdown ?? []).filter((a) => a.completionRate != null);
    const byRate = [...started].sort((a, b) => b.completionRate - a.completionRate).slice(0, 8);
    const byVolume = [...started].sort((a, b) => b.startCount - a.startCount).slice(0, 8);
    return { byRate, byVolume };
  }, [contentSummary]);
  const completionRows = completionView === "rate" ? ranked.byRate : ranked.byVolume;
  const hasCompletionData = ranked.byRate.length > 0;

  const dailyActives = engagement?.dailyActiveTeachers ?? [];
  const retention = engagement?.retention;
  const hasEngagementData = !!engagement && (dailyActives.length > 0 || !!retention);

  // Sign-in method merged into the trend itself rather than a separate donut
  // next to it — every method that shows up on any day in range becomes its
  // own stacked series, in a fixed order so the same colour always means the
  // same method as a teacher scrubs across weeks.
  const authMethodKeys = useMemo(() => {
    const order = ["PASSWORD", "GOOGLE", "CLEVER", "MULTIPLE", "OTHER"];
    const present = new Set();
    dailyActives.forEach((d) => Object.keys(d.byMethod || {}).forEach((k) => present.add(k)));
    return order.filter((k) => present.has(k));
  }, [dailyActives]);

  return (
    <div className="p-4 mt-3">
      {!loading && !hasData ? (
        <motion.div initial="hidden" animate="visible" variants={panelVariants}>
          <EmptyState title={typography.OVERVIEW_EMPTY} detail={typography.OVERVIEW_EMPTY_DETAIL} />
        </motion.div>
      ) : (
        <>
          <Row>
            <HeroStatCard
              title={typography.COMPLETED_RESETS}
              infoText="Resets with ClassActivityOutcome.completed = true, in the selected range."
              value={data?.completedResets}
              loading={loading}
              contextText={data ? `of ${data.totalResets} resets in range` : ""}
              rows={
                data
                  ? [
                      { label: "Total Resets", value: data.totalResets },
                      { label: "With Logged Outcome", value: data.resetsWithOutcome },
                    ]
                  : []
              }
            />
            <HeroStatCard
              title={typography.ACTIVE_TEACHERS}
              infoText="role:Educator teachers who ran at least one reset in the selected range."
              value={data?.activeTeachers}
              loading={loading || adoptionLoading}
              contextText={adoption ? `${adoption.repeatUse.atLeastTwoRestsPct}% ran 2+ resets ever` : ""}
              rows={
                adoption
                  ? [
                      { label: typography.REPEAT_USE_PCT, value: `${adoption.repeatUse.atLeastTwoRestsPct}%` },
                      { label: typography.ACTIVE_LAST_14_PCT, value: `${adoption.repeatUse.activeLast14DaysPct}%` },
                    ]
                  : []
              }
            />
            <HeroStatCard
              title={typography.ACTIVE_CLASSROOMS}
              infoText="Distinct sections with at least one reset in the selected range."
              value={data?.activeClassrooms}
              loading={loading}
              contextText={visibleSchools.length ? `across ${visibleSchools.length} school${visibleSchools.length === 1 ? "" : "s"}` : ""}
              rows={
                visibleSchools.length
                  ? [
                      { label: "Distinct Districts", value: districtCount },
                      ...(topSchool ? [{ label: `Top School — ${topSchool.schoolName}`, value: topSchool.completedResets }] : []),
                    ]
                  : []
              }
            />
          </Row>

          <motion.div initial="hidden" animate="visible" variants={panelVariants} className="mcap-impl-panels">
            <div className="mcap-impl-card">
              <div className="mcap-impl-card-head">
                <div>
                  <h5 className="mcap-impl-card-title mb-0">{typography.OVERVIEW_MOMENTS_TITLE}</h5>
                  <p className="standard-typo-12-400-ld mb-0">{typography.OVERVIEW_MOMENTS_DETAIL}</p>
                </div>
              </div>
              {!usageLoading && momentSegments.length === 0 ? (
                <p className="standard-typo-14-400-ld mcap-impl-empty">{typography.OVERVIEW_MOMENTS_EMPTY}</p>
              ) : (
                <DonutChart segments={momentSegments} centerValue={totalMomentResets} centerLabel="resets" />
              )}
            </div>

            <div className="mcap-impl-card">
              <div className="mcap-impl-card-head">
                <div>
                  <h5 className="mcap-impl-card-title mb-0">{typography.OVERVIEW_COMPLETION_TITLE}</h5>
                  <p className="standard-typo-12-400-ld mb-0">{typography.OVERVIEW_COMPLETION_DETAIL}</p>
                </div>
                <div className="mcap-sort">
                  {[
                    { key: "rate", label: typography.OVERVIEW_COMPLETION_STARTS_TAB },
                    { key: "counts", label: typography.OVERVIEW_COMPLETION_COUNTS_TAB },
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      type="button"
                      className={`mcap-sort-btn${completionView === key ? " is-active" : ""}`}
                      onClick={() => setCompletionView(key)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              {!contentLoading && !hasCompletionData ? (
                <p className="standard-typo-14-400-ld mcap-impl-empty">{typography.OVERVIEW_COMPLETION_EMPTY}</p>
              ) : (
                <ReactApexChart
                  type="bar"
                  // Fixed at 260 regardless of row count, this looked fine
                  // with 8 activities and absurd with the 2 the demo data
                  // actually has — two bars stretched to fill a 260px plot
                  // area each get a 130px-tall slot, reading as huge bars
                  // with a canyon of empty space between them. Same fix
                  // Engagement.js's own horizontal bar list already uses:
                  // scale with row count so each bar's slot stays constant.
                  height={Math.max(180, completionRows.length * 46)}
                  series={
                    completionView === "rate"
                      ? [{ name: "Completion rate", data: completionRows.map((a) => a.completionRate) }]
                      : [
                          { name: "Starts", data: completionRows.map((a) => a.startCount) },
                          { name: "Finishes", data: completionRows.map((a) => a.completionCount) },
                        ]
                  }
                  options={{
                    chart: { toolbar: { show: false } },
                    colors: completionView === "rate" ? [MOMENT_COLORS.MORNING_MEETING] : [MOMENT_COLORS.TRANSITION, MOMENT_COLORS.MORNING_MEETING],
                    plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: "55%" } },
                    dataLabels: { enabled: false },
                    xaxis: {
                      categories: completionRows.map((a) => a.title),
                      max: completionView === "rate" ? 100 : undefined,
                      labels: { style: { fontSize: "10px", fontFamily: "Poppins" }, formatter: completionView === "rate" ? (v) => `${v}%` : undefined },
                    },
                    yaxis: { labels: { style: { fontSize: "11px", fontFamily: "Poppins" } } },
                    grid: { borderColor: GRAPH_COLORS.gridBorder },
                    legend: completionView === "counts" ? { position: "top", fontSize: "12px", fontFamily: "Poppins" } : { show: false },
                    // Custom tooltip rather than Apex's default: the bar only
                    // ever shows one measure, but the data behind each activity
                    // is richer than that — finishers and average watch time
                    // are worth surfacing on hover without spending chart space
                    // on them all the time.
                    tooltip: {
                      custom: ({ dataPointIndex }) => {
                        const row = completionRows[dataPointIndex];
                        if (!row) return "";
                        const mins = row.avgDurationSeconds != null ? Math.round(row.avgDurationSeconds / 60) : null;
                        return `<div class="mcap-chart-tooltip">
                          <strong>${row.title}</strong>
                          <span>${row.completionCount} of ${row.startCount} starts finished</span>
                          <span>${row.uniqueFinishers} unique finisher${row.uniqueFinishers === 1 ? "" : "s"}</span>
                          ${mins != null ? `<span>~${mins} min average</span>` : ""}
                        </div>`;
                      },
                    },
                  }}
                />
              )}
            </div>
          </motion.div>

          <motion.div initial="hidden" animate="visible" variants={panelVariants} className="mcap-impl-card mcap-impl-wide mt-3">
            <div className="mcap-impl-card-head">
              <div>
                <h5 className="mcap-impl-card-title mb-0">{typography.OVERVIEW_ENGAGEMENT_TITLE}</h5>
                <p className="standard-typo-12-400-ld mb-0">{typography.OVERVIEW_ENGAGEMENT_DETAIL}</p>
              </div>
              <div className="mcap-sort">
                {[
                  { key: "trend", label: typography.OVERVIEW_ENGAGEMENT_TREND_TAB },
                  { key: "retention", label: typography.OVERVIEW_ENGAGEMENT_RETENTION_TAB },
                ].map(({ key, label }) => (
                  <button
                    key={key}
                    type="button"
                    className={`mcap-sort-btn${engagementView === key ? " is-active" : ""}`}
                    onClick={() => setEngagementView(key)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Each view explains itself in one line before the chart, not
                just a chart title — "Retention" on its own told a reader
                nothing about which teachers it counts or what "week 2" means. */}
            {!engagementLoading && !hasEngagementData ? (
              <p className="standard-typo-14-400-ld mcap-impl-empty">{typography.OVERVIEW_ENGAGEMENT_EMPTY}</p>
            ) : engagementView === "trend" ? (
              <>
                {/* Sign-in method is stacked into the same trend rather than
                    a separate chart — the coloured bands are the composition,
                    the total height is the same daily-actives line this used
                    to be alone. */}
                <p className="standard-typo-12-400-ld mcap-chart-subtitle mb-2">{typography.OVERVIEW_AUTH_EXPLAINER}</p>
                <ReactApexChart
                  type="area"
                  height={260}
                  series={authMethodKeys.map((method) => ({
                    name: AUTH_METHOD_LABELS[method] || method,
                    data: dailyActives.map((d) => d.byMethod?.[method] ?? 0),
                  }))}
                  options={{
                    chart: { toolbar: { show: false }, stacked: true },
                    colors: authMethodKeys.map((method) => AUTH_METHOD_COLORS[method] || AUTH_METHOD_COLORS.OTHER),
                    fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.55, opacityTo: 0.15, stops: [0, 100] } },
                    dataLabels: { enabled: false },
                    stroke: { curve: "smooth", width: 1.5 },
                    xaxis: {
                      categories: dailyActives.map((d) => d.date),
                      labels: { style: { fontSize: "10px", fontFamily: "Poppins" }, rotate: -45 },
                      axisBorder: { show: false },
                      axisTicks: { show: false },
                    },
                    yaxis: { min: 0 },
                    legend: { position: "top", fontSize: "11px", fontFamily: "Poppins" },
                    grid: { borderColor: GRAPH_COLORS.gridBorder },
                    tooltip: { y: { formatter: (v) => `${v} teacher${v === 1 ? "" : "s"}` } },
                  }}
                />
              </>
            ) : (
              <>
                <p className="standard-typo-12-400-ld mcap-chart-subtitle mb-2">{typography.OVERVIEW_RETENTION_EXPLAINER}</p>
                <ReactApexChart
                  type="bar"
                  height={240}
                  series={[
                    {
                      name: "Came back",
                      data: retention ? [retention.week_0, retention.week_1, retention.week_2, retention.week_3, retention.week_4] : [],
                    },
                  ]}
                  options={{
                    chart: { toolbar: { show: false } },
                    colors: [MOMENT_COLORS.RETURN_FROM_RECESS],
                    plotOptions: { bar: { borderRadius: 4, columnWidth: "45%" } },
                    dataLabels: { enabled: true, formatter: (v) => `${v}%`, style: { fontSize: "11px", fontFamily: "Poppins" } },
                    xaxis: {
                      categories: ["Week of first visit", "1 week later", "2 weeks later", "3 weeks later", "4 weeks later"],
                      labels: { style: { fontSize: "10px", fontFamily: "Poppins" } },
                    },
                    yaxis: { min: 0, max: 100, labels: { formatter: (v) => `${v}%` } },
                    grid: { borderColor: GRAPH_COLORS.gridBorder },
                    tooltip: { y: { formatter: (v) => `${v}% of that first week's teachers` } },
                  }}
                />
              </>
            )}
          </motion.div>

          {(adoptionLoading || hasAdoption) && (
            <>
              <motion.div initial="hidden" animate="visible" variants={panelVariants}>
                <Card className="mt-3">
                  <CardBody>
                    <h5 className="mb-1 font-family-lexend standard-typo-14-500-ld">{typography.WEEKLY_ADOPTION_TITLE}</h5>
                    <p className="standard-typo-12-400-ld mcap-chart-subtitle mb-3">{typography.ADOPTION_SUPPRESSED_NOTE}</p>
                    <ReactApexChart
                      type="line"
                      height={280}
                      series={[
                        { name: typography.WEEKLY_ACTIVE_TEACHERS_SERIES, data: weekly.aValues },
                        { name: typography.WEEKLY_ACTIVATIONS_SERIES, data: weekly.bValues },
                      ]}
                      options={{
                        chart: { toolbar: { show: false } },
                        colors: [GRAPH_COLORS.teal, GRAPH_COLORS.orange],
                        dataLabels: { enabled: false },
                        stroke: { curve: "smooth", width: 2 },
                        connectNulls: false,
                        xaxis: {
                          categories: weekly.weeks,
                          labels: { style: { fontSize: "10px", fontFamily: "Poppins" }, rotate: -45 },
                          axisBorder: { show: false },
                          axisTicks: { show: false },
                        },
                        yaxis: { min: 0 },
                        grid: { borderColor: GRAPH_COLORS.gridBorder },
                        legend: { position: "top", fontSize: "12px", fontFamily: "Poppins" },
                      }}
                    />
                  </CardBody>
                </Card>
              </motion.div>
            </>
          )}
        </>
      )}
    </div>
  );
}
